/**
 * Firestore アクセス抽象化レイヤー (フロントエンド)
 *
 * 全てのFirestoreアクセスをこのサービス経由で行うことで、
 * マルチ試験対応のDB構造に簡単に移行できます。
 *
 * 使い方:
 * ```typescript
 * import { getCurrentExamCollection, getUsersCollection } from '@/services/firestoreService';
 *
 * // 試験固有のコレクション
 * const questionsRef = getCurrentExamCollection('exam_questions');
 *
 * // 全試験共通のusersコレクション
 * const usersRef = getUsersCollection();
 * ```
 */

import { db } from '@/firebase';
import { collection, doc, CollectionReference, DocumentReference } from 'firebase/firestore';
import { CURRENT_APP_ID } from '@/config/exam';

/**
 * 現在のアプリのコレクションを取得
 *
 * @param collectionName - コレクション名 (例: 'exam_questions', 'subscriptions')
 * @param appId - アプリID (省略時はCURRENT_APP_IDを使用)
 * @returns CollectionReference
 */
export function getCurrentExamCollection(collectionName: string, appId?: string): CollectionReference {
  const targetAppId = appId || CURRENT_APP_ID;
  return collection(db, 'apps', targetAppId, collectionName);
}

/**
 * 指定したアプリのコレクションを取得
 *
 * @param appId - アプリID (例: 'chusho-app')
 * @param collectionName - コレクション名
 * @returns CollectionReference
 */
export function getExamCollection(appId: string, collectionName: string): CollectionReference {
  return collection(db, 'apps', appId, collectionName);
}

/**
 * 現在のアプリのドキュメントを取得
 *
 * @param collectionName - コレクション名
 * @param docId - ドキュメントID
 * @param appId - アプリID (省略時はCURRENT_APP_IDを使用)
 * @returns DocumentReference
 */
export function getCurrentExamDoc(collectionName: string, docId: string, appId?: string): DocumentReference {
  const targetAppId = appId || CURRENT_APP_ID;
  return doc(db, 'apps', targetAppId, collectionName, docId);
}

/**
 * usersコレクションを取得（全試験共通）
 *
 * @returns CollectionReference
 */
export function getUsersCollection(): CollectionReference {
  return collection(db, 'users');
}

/**
 * 特定のユーザードキュメントを取得
 *
 * @param userId - ユーザーID
 * @returns DocumentReference
 */
export function getUserDoc(userId: string): DocumentReference {
  return doc(db, 'users', userId);
}

/**
 * 現在のアプリのメタデータドキュメントを取得
 *
 * @param appId - アプリID (省略時はCURRENT_APP_IDを使用)
 * @returns DocumentReference
 */
export function getCurrentExamMetadata(appId?: string): DocumentReference {
  const targetAppId = appId || CURRENT_APP_ID;
  return doc(db, 'apps', targetAppId);
}

/**
 * アプリIDを取得
 *
 * @returns 現在のアプリID
 */
export function getCurrentExamId(): string {
  return CURRENT_APP_ID;
}

/**
 * レガシー互換性: 移行前の直接アクセスをサポート
 * TODO: 移行完了後にこの関数は削除する
 *
 * @deprecated 代わりに getCurrentExamCollection を使用してください
 */
export function getLegacyCollection(collectionName: string): CollectionReference {
  return collection(db, collectionName);
}

/**
 * レガシー互換性: 移行前の直接アクセスをサポート
 * TODO: 移行完了後にこの関数は削除する
 *
 * @deprecated 代わりに getCurrentExamDoc を使用してください
 */
export function getLegacyDoc(collectionName: string, docId: string): DocumentReference {
  return doc(db, collectionName, docId);
}
